import React from 'react';
import { formatMoney } from '../../utils/format';
import { CheckCircle2, X, AlertCircle, Truck } from 'lucide-react';
import { useTranslation } from '../../i18n';

/**
 * Confirmation modal shown BEFORE engaging a leasing contract. Prevents the
 * double-click bug where two identical trucks were leased back-to-back.
 */
export const LeaseConfirmModal = ({ vehicleData, durationMonths, onConfirm, onClose, treasury }) => {
  const { t } = useTranslation();

  const deposit = Math.round(vehicleData.price * 0.10);
  const monthlyLease = Math.round((vehicleData.price * 0.90) / durationMonths * 1.08);
  const residualValue = Math.round(vehicleData.price * 0.25);
  const totalEngagement = deposit + monthlyLease * durationMonths;
  const canAfford = treasury >= deposit;

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="lease-confirm-overlay">
      <div className="bg-ink-800 rounded-xl w-full max-w-md shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-200" data-testid="lease-confirm-modal">
        <div className="bg-purple-600 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Truck className="w-5 h-5" />
            <h3 className="font-bold text-base">{t('confirm_lease_title')}</h3>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="close-lease-confirm">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <p className="text-sm font-semibold text-slate-200">{vehicleData.brand} {vehicleData.model}</p>
            <p className="text-[11px] text-slate-500">Tier {vehicleData.tier} · {durationMonths} mois</p>
          </div>

          <p className="text-[12px] text-slate-400 leading-snug">{t('confirm_lease_intro')}</p>

          <div className="rounded-md border border-ink-600 bg-ink-700/30 divide-y divide-ink-600 text-xs">
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-slate-400">{t('confirm_lease_deposit')}</span>
              <span className="font-bold text-danger" data-testid="lease-deposit">{formatMoney(deposit)}</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-slate-400">{t('confirm_lease_monthly')}</span>
              <span className="font-bold text-slate-200" data-testid="lease-monthly">{formatMoney(monthlyLease)}/mois</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-slate-400">{t('confirm_lease_duration')}</span>
              <span className="font-bold text-slate-200">{durationMonths} mois</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-slate-400">{t('confirm_lease_residual')}</span>
              <span className="font-bold text-slate-200">{formatMoney(residualValue)}</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2.5 bg-purple-500/10">
              <span className="text-sm font-semibold text-purple-300">{t('confirm_lease_total_engagement')}</span>
              <span className="text-base font-black text-purple-300" data-testid="lease-total-engagement">{formatMoney(totalEngagement)}</span>
            </div>
          </div>

          {!canAfford && (
            <div className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-danger flex-shrink-0" />
              <p className="text-[12px] text-danger">Acompte de {formatMoney(deposit)} requis. Trésorerie insuffisante.</p>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-ink-600 bg-ink-700/30 flex items-center justify-between">
          <button onClick={onClose} className="text-xs text-slate-400 hover:text-slate-200 px-3 py-2 transition-colors" data-testid="cancel-lease-confirm">
            {t('cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={!canAfford}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="confirm-lease-signing-btn"
          >
            <CheckCircle2 className="w-4 h-4" />
            {t('confirm_lease_confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeaseConfirmModal;
